import { useMemo, useState } from 'react';

import { Table } from '@tanstack/react-table';

import { Button } from '../shadcn/button';
import { ButtonGroup } from '../shadcn/button-group';
import { Input } from '../shadcn/input';

export function TablePagination<T>({
  table,
  plural,
  className,
  variant = 'default',
  totalCount,
}: {
  table: Table<T>;
  plural: string;
  className?: string;
  variant?: 'sm' | 'default';
  totalCount?: number; // For server-side pagination
}) {
  const currentPage = table.getState().pagination.pageIndex + 1;
  const [pageString, setPageString] = useState(currentPage.toString());
  const [isEditing, setIsEditing] = useState(false);

  // Use server-provided totalCount if available (server-side pagination)
  // Otherwise fall back to client-side row count
  const rowCount = totalCount ?? table.getFilteredRowModel().rows.length;
  const isServerSide = totalCount !== undefined;

  const totalPages = useMemo(
    () => (isServerSide ? table.getPageCount() : Math.ceil(rowCount / table.getState().pagination.pageSize)),
    [isServerSide, rowCount, table.getPageCount(), table.getState().pagination.pageSize],
  );

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const target = e.target as HTMLInputElement;

    if (e.key === 'Enter') {
      const pageNumber = parseInt(pageString);

      if (!pageString || isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
        setPageString(currentPage.toString());
      } else {
        table.setPageIndex(pageNumber - 1);
      }

      setIsEditing(false);
      target.blur();
    }
  }

  function onBlur() {
    setIsEditing(false);
    setPageString(currentPage.toString());
  }

  function onFocus() {
    setIsEditing(true);
    setPageString(currentPage.toString());
  }

  return (
    <div
      className={`flex items-center ${className} ${variant === 'sm' ? 'text-xs' : 'my-6 w-full justify-between text-sm'}`}
    >
      <div className="mx-2 text-gray-500">
        Showing{' '}
        {rowCount > 0 && (
          <span>
            {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
            {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, rowCount)}{' '}
            of{' '}
          </span>
        )}
        <span className="font-medium text-gray-600">
          {rowCount.toLocaleString()} {plural}
        </span>
      </div>

      <div className="flex items-center gap-1.5 text-gray-600">
        <p className="mx-2">
          <span>Page</span>
          <Input
            type="number"
            min={1}
            max={totalPages}
            value={isEditing ? pageString : totalPages === 0 ? 0 : currentPage}
            onChange={(e) => setPageString(e.target.value)}
            disabled={!rowCount || totalPages <= 1}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            className={`mx-1.5 inline-flex h-auto w-auto px-2 py-1 ${variant === 'sm' ? 'text-xs' : 'text-sm'}`}
          />
          of {totalPages}
        </p>

        <ButtonGroup>
          <Button
            size={variant}
            onClick={(e) => {
              e.preventDefault();
              setPageString(table.getState().pagination.pageIndex.toString());
              table.previousPage();
            }}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size={variant}
            onClick={(e) => {
              e.preventDefault();
              setPageString((table.getState().pagination.pageIndex + 2).toString());
              table.nextPage();
            }}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
}
